import React, { useCallback, useContext, useState } from 'react';
import { Button, Modal } from 'antd';

import {
  Building,
  BuildingMoveRequest,
} from '@totem/components/orgManagement/orgDetails/buildings/applied/types';
import OrgHierarchyContainer from '@totem/components/orgManagement/uiComponents/hierarchySelector/orgHierarchyContainer';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { ObjectReference } from '@totem/types/common';
import { isNotNull } from '@totem/utilities/common';
import { ORGANIZATIONS_ENDPOINT } from '@totem/utilities/endpoints';
import logger from '@totem/utilities/logger';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

const styles = {
  dialog: {
    maxHeight: '80%',
    maxWidth: '850px',
  },
  label: {
    fontSize: '12px',
    fontWeight: 600,
  },
};

type Props = {
  currentBuilding: Building;
  open: boolean;
  onClose: (refresh: boolean) => void;
};

const BuildingMoveDialog = ({ open, onClose, currentBuilding }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [isSending, setIsSending] = useState(false);
  const [selectedOrganization, setSelectedOrganization] =
    useState<ObjectReference>(null);
  const [selectedRegion, setSelectedRegion] = useState<ObjectReference>(null);

  const sendBuildingMove = useCallback(
    async (payload: BuildingMoveRequest) => {
      if (!isSending) {
        fetch(`${ORGANIZATIONS_ENDPOINT}/hierarchy/building/move`, {
          method: 'POST',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
          }),
          body: JSON.stringify(payload),
        }).then((res) => {
          setIsSending(false);
          const success = CheckResponseShowError(res);
          if (success) {
            onClose(true);
          }
        });
      }
    }, // End async
    [],
  );

  const handleSubmit = () => {
    if (!isNotNull(selectedOrganization) || !isNotNull(selectedRegion)) {
      return;
    }

    Modal.confirm({
      title: 'Are you sure?',
      content: `Moving a building should be taken lightly.  This may require updates to the tools such as moving the building to a new Tenant,  update tenant mappings, etc..  Are you SURE you want to move ${currentBuilding.name} to organization ${selectedOrganization.name} under region ${selectedRegion.name}?`,
      okText: 'Move Building',
      okType: 'danger',
      cancelText: 'Cancel',
      open: false,
      onOk: () => {
        setIsSending(true);

        const payload: BuildingMoveRequest = {
          buildingId: currentBuilding.id,
          toOrganizationId: selectedOrganization.id,
          toRegionId: selectedRegion.id,
        };

        sendBuildingMove(payload);

        setIsSending(false);
      },
    });
  };

  const handleHierarchSelected = useCallback(
    (
      organization: ObjectReference | null,
      region: ObjectReference | null,
      _building: ObjectReference | null,
    ) => {
      setSelectedOrganization((previous) =>
        previous?.id === organization?.id ? previous : organization,
      );

      setSelectedRegion((previous) =>
        previous?.id === region?.id ? previous : region,
      );

      if (
        isNotNull(organization) &&
        isNotNull(region) &&
        isNotNull(currentBuilding)
      ) {
        logger.debug(
          `Move ${currentBuilding.name} to ${organization.name} / ${region.name}`,
        );
      }
    },
    [currentBuilding],
  );

  return (
    <Modal
      open={open}
      onCancel={() => onClose(false)}
      title={<ModalTitle>Move Building ({currentBuilding.name})</ModalTitle>}
      confirmLoading={false}
      style={styles.dialog}
      footer={
        <ModalFooter>
          <Button onClick={() => onClose(false)}>Close</Button>
          <Button onClick={handleSubmit} type="primary" disabled={isSending}>
            Submit
          </Button>
        </ModalFooter>
      }
    >
      <OrgHierarchyContainer
        showRegionSelect
        showBuildingSelect={false}
        onSelected={handleHierarchSelected}
      />
    </Modal>
  );
};

export default BuildingMoveDialog;
